import React, { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { LogIn } from "lucide-react";

export default function Login() {
  const { user, login, error } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const nav = useNavigate();

  if (user && user !== false) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const ok = await login(email, password);
    setSubmitting(false);
    if (ok) nav("/", { replace: true });
  };

  return (
    <div className="min-h-screen relative bg-slate-900 flex items-stretch">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage:
            "url(https://images.unsplash.com/photo-1762701249025-a80d32e3cf46?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600)",
        }}
        aria-hidden
      />
      <div className="absolute inset-0 bg-slate-900/85" aria-hidden />

      <div className="relative w-full grid place-items-center p-4">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-6">
            <img
              src="/powermove-mark.png"
              alt="Material Tracker"
              className="h-12 w-12 rounded-sm object-contain bg-slate-950 border border-slate-800"
            />
            <div className="text-white">
              <div className="font-display text-2xl uppercase tracking-wider">
                Material
              </div>
              <div className="text-xs uppercase tracking-[0.2em] text-amber-400">
                Tracker
              </div>
            </div>
          </div>

          <div className="rg-card p-6 sm:p-8">
            <h1 className="font-display text-2xl text-slate-900 uppercase tracking-tight mb-1">
              Sign in
            </h1>
            <p className="text-sm text-slate-600 mb-6">
              Yard, truck, and substation tracking.
            </p>

            <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                  Email
                </label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email-input"
                  className="rg-tap w-full border-2 border-slate-300 rounded-sm px-3 text-base bg-white focus:border-slate-900 outline-none"
                  placeholder="you@rougee.com"
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                  Password
                </label>
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password-input"
                  className="rg-tap w-full border-2 border-slate-300 rounded-sm px-3 text-base bg-white focus:border-slate-900 outline-none"
                  placeholder="••••••••"
                />
              </div>

              {error && (
                <div
                  className="border-2 border-rose-600 bg-rose-50 text-rose-800 text-sm font-bold px-3 py-2 rounded-sm"
                  data-testid="login-error"
                >
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                data-testid="login-submit-btn"
                className="rg-tap-xl w-full bg-slate-900 hover:bg-slate-800 text-white font-display uppercase tracking-wider rounded-sm flex items-center justify-center gap-2 disabled:opacity-60"
              >
                <LogIn strokeWidth={3} className="h-5 w-5" />
                {submitting ? "Signing in…" : "Sign in"}
              </button>
            </form>

            <div className="mt-6 pt-6 border-t-2 border-slate-200 text-sm text-slate-700 text-center">
              No account?{" "}
              <Link
                to="/register"
                data-testid="link-register"
                className="font-bold underline text-slate-900"
              >
                Create one
              </Link>
            </div>
          </div>

          <div className="mt-4 text-center text-xs text-slate-300">
            Internal use only · Stop materials from getting lost in the sauce.
          </div>
        </div>
      </div>
    </div>
  );
}
